import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { ArrowLeft, CheckCircle, AlertTriangle, Lightbulb, Target, Clock, Brain, TrendingUp } from "lucide-react";

interface BestPracticesProps {
  onBackToLanding: () => void;
}

const practiceCategories = [
  {
    icon: Target,
    title: "Decision Quality",
    description: "How to make better decisions from the start",
    color: "bg-blue-100 text-blue-700"
  },
  {
    icon: Clock,
    title: "Timing & Reviews",
    description: "When and how to review your decisions",
    color: "bg-green-100 text-green-700"
  },
  {
    icon: Brain,
    title: "Cognitive Awareness",
    description: "Recognizing and avoiding bias",
    color: "bg-purple-100 text-purple-700"
  },
  {
    icon: TrendingUp,
    title: "Continuous Improvement",
    description: "Learning from patterns and outcomes",
    color: "bg-orange-100 text-orange-700"
  }
];

const bestPractices = [
  {
    category: "Decision Quality",
    icon: Target,
    practices: [
      {
        title: "Define the Real Problem",
        description: "Before jumping to solutions, spend time clearly articulating what you're actually trying to solve.",
        tip: "Ask 'What problem am I really trying to solve?' three times to get to the root issue.",
        difficulty: "Easy"
      },
      {
        title: "Consider Multiple Options",
        description: "Resist the urge to jump between just two choices. Generate at least 3-5 viable alternatives.",
        tip: "Use the 'What else?' question to push beyond your first few ideas.",
        difficulty: "Medium"
      },
      {
        title: "Set Clear Success Criteria",
        description: "Define what 'good' looks like before you decide, so you can evaluate objectively later.",
        tip: "Write down 3-5 specific outcomes that would make this decision successful.",
        difficulty: "Medium"
      }
    ]
  },
  {
    category: "Timing & Reviews",
    icon: Clock,
    practices: [
      {
        title: "Use the 10-10-10 Rule",
        description: "Consider how you'll feel about this decision in 10 minutes, 10 months, and 10 years.",
        tip: "This helps balance short-term and long-term thinking in your decision process.",
        difficulty: "Easy"
      },
      {
        title: "Schedule Strategic Reviews",
        description: "Set review dates based on when you expect to see meaningful outcomes, not arbitrary timelines.",
        tip: "For career decisions, review at 30 and 90 days. For investments, review quarterly.",
        difficulty: "Medium"
      },
      {
        title: "Capture Context Immediately",
        description: "Record the circumstances, emotions, and constraints while they're fresh in your memory.",
        tip: "Add a voice memo immediately after making big decisions to capture your reasoning.",
        difficulty: "Easy"
      }
    ]
  },
  {
    category: "Cognitive Awareness",
    icon: Brain,
    practices: [
      {
        title: "Identify Your Emotional State",
        description: "Acknowledge how you're feeling when making decisions, as emotions significantly impact judgment.",
        tip: "Rate your stress, excitement, and confidence levels from 1-10 when logging decisions.",
        difficulty: "Medium"
      },
      {
        title: "Seek Diverse Perspectives",
        description: "Actively look for viewpoints that challenge your initial thinking and assumptions.",
        tip: "Ask someone who disagrees with you to explain their reasoning before deciding.",
        difficulty: "Hard"
      },
      {
        title: "Use Pre-mortem Analysis",
        description: "Imagine your decision failed spectacularly and work backwards to identify potential risks.",
        tip: "Ask 'What could go wrong?' and 'What would cause this to fail completely?'",
        difficulty: "Medium"
      }
    ]
  },
  {
    category: "Continuous Improvement",
    icon: TrendingUp,
    practices: [
      {
        title: "Track Decision Patterns",
        description: "Look for recurring themes in your successful and unsuccessful decisions.",
        tip: "Monthly, review your decisions and note patterns in timing, stress levels, or decision types.",
        difficulty: "Hard"
      },
      {
        title: "Celebrate Good Process",
        description: "Reward yourself for following good decision-making processes, regardless of outcomes.",
        tip: "Good decisions can have bad outcomes due to luck. Focus on improving your process.",
        difficulty: "Medium"
      },
      {
        title: "Learn from Near Misses",
        description: "Pay attention to decisions that almost went badly - they're rich sources of learning.",
        tip: "Ask 'What early warning signs did I ignore?' and 'What could I have done differently?'",
        difficulty: "Hard"
      }
    ]
  }
];

const commonMistakes = [
  {
    mistake: "Making decisions when highly emotional",
    solution: "Use the 24-hour rule for big decisions. Sleep on it when emotions are running high.",
    severity: "high"
  },
  {
    mistake: "Only considering financial factors",
    solution: "Include quality of life, relationships, and personal growth in your evaluation criteria.",
    severity: "medium"
  },
  {
    mistake: "Avoiding decisions that feel uncertain",
    solution: "Accept that uncertainty is normal. Focus on gathering enough information, not perfect information.",
    severity: "medium"
  },
  {
    mistake: "Not setting review reminders",
    solution: "Always schedule follow-up reviews when making the initial decision, not after.",
    severity: "high"
  },
  {
    mistake: "Changing decisions too quickly after setbacks",
    solution: "Distinguish between temporary obstacles and fundamental flaws in your reasoning.",
    severity: "medium"
  }
];

export function BestPractices({ onBackToLanding }: BestPracticesProps) {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBackToLanding}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Home</span>
              </Button>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="text-2xl font-semibold">Best Practices</h1>
                <p className="text-sm text-muted-foreground">
                  Guidelines for effective decision making and journaling
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-medium mb-4">
            Master Your Decision-Making Process
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Research-backed strategies and insights to help you make better decisions 
            and learn more effectively from every choice you make.
          </p>
        </div>

        {/* Practice Categories Overview */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {practiceCategories.map((category, index) => (
            <Card key={index} className="text-center hover:shadow-md transition-shadow">
              <CardHeader>
                <div className={`w-12 h-12 ${category.color} rounded-lg flex items-center justify-center mx-auto mb-4`}>
                  <category.icon className="h-6 w-6" />
                </div>
                <CardTitle className="text-lg">{category.title}</CardTitle>
                <CardDescription>{category.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>

        {/* Best Practices by Category */}
        {bestPractices.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-16">
            <div className="flex items-center mb-8">
              <div className={`p-2 ${practiceCategories[categoryIndex].color} rounded-lg mr-3`}>
                <category.icon className="h-5 w-5" />
              </div>
              <h3 className="text-2xl font-medium">{category.category}</h3>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.practices.map((practice, practiceIndex) => (
                <Card key={practiceIndex} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{practice.title}</CardTitle>
                      <Badge variant={practice.difficulty === "Hard" ? "destructive" : practice.difficulty === "Medium" ? "default" : "secondary"}>
                        {practice.difficulty}
                      </Badge>
                    </div>
                    <CardDescription className="leading-relaxed">
                      {practice.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <Lightbulb className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h5 className="font-medium text-sm mb-1">Pro Tip</h5>
                          <p className="text-sm text-muted-foreground">{practice.tip}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

        {/* Common Mistakes */}
        <div className="mb-16">
          <h3 className="text-2xl font-medium mb-8 flex items-center">
            <AlertTriangle className="h-6 w-6 mr-3 text-orange-500" />
            Common Mistakes to Avoid
          </h3>
          <div className="space-y-4">
            {commonMistakes.map((item, index) => (
              <Card key={index} className="border-l-4 border-l-orange-500">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-medium text-orange-900">❌ {item.mistake}</h4>
                        <Badge variant={item.severity === "high" ? "destructive" : "secondary"}>
                          {item.severity} impact
                        </Badge>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <p className="text-muted-foreground">
                          <span className="font-medium text-green-700">Solution:</span> {item.solution}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Decision-Making Framework */}
        <Card className="mb-16 bg-gradient-to-r from-primary/5 to-primary/10">
          <CardHeader>
            <CardTitle className="text-2xl text-center">The DECIDE Framework</CardTitle>
            <CardDescription className="text-center">
              A systematic approach to making better decisions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { step: "D", title: "Define the Problem", description: "Clearly articulate what needs to be decided" },
                { step: "E", title: "Establish Criteria", description: "Set the standards for evaluating options" },
                { step: "C", title: "Consider Alternatives", description: "Generate multiple viable options" },
                { step: "I", title: "Identify Best Option", description: "Evaluate alternatives against criteria" },
                { step: "D", title: "Develop Action Plan", description: "Create implementation strategy" },
                { step: "E", title: "Evaluate & Monitor", description: "Track outcomes and learn" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 font-semibold">
                    {item.step}
                  </div>
                  <h4 className="font-medium mb-2">{item.title}</h4>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Reference Card */}
        <Card className="bg-muted/30">
          <CardHeader>
            <CardTitle className="text-center">Quick Reference: Decision Journal Entry</CardTitle>
            <CardDescription className="text-center">
              Essential elements to include in every decision log
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-medium mb-4 text-green-700">✅ Always Include</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-600 mr-2" />Clear decision statement</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-600 mr-2" />Your reasoning/rationale</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-600 mr-2" />Context and constraints</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-600 mr-2" />Emotional state</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-600 mr-2" />Initial confidence level</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-600 mr-2" />Success criteria</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-600 mr-2" />Review schedule</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-4 text-blue-700">💡 Consider Adding</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><Lightbulb className="h-4 w-4 text-blue-600 mr-2" />Alternatives considered</li>
                  <li className="flex items-center"><Lightbulb className="h-4 w-4 text-blue-600 mr-2" />Key assumptions made</li>
                  <li className="flex items-center"><Lightbulb className="h-4 w-4 text-blue-600 mr-2" />Potential risks identified</li>
                  <li className="flex items-center"><Lightbulb className="h-4 w-4 text-blue-600 mr-2" />People consulted</li>
                  <li className="flex items-center"><Lightbulb className="h-4 w-4 text-blue-600 mr-2" />Time pressure factors</li>
                  <li className="flex items-center"><Lightbulb className="h-4 w-4 text-blue-600 mr-2" />Relevant tags/categories</li>
                  <li className="flex items-center"><Lightbulb className="h-4 w-4 text-blue-600 mr-2" />Related decisions</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16">
        <div className="container py-8">
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2025 Decisionr.xyz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}